import {
  CANADA_COUNTRY_VALUE,
  CANADA_PROVINCES,
  US_STATES,
  US_VIRGIN_ISLANDS_COUNTRY_VALUE,
  US_VIRGIN_ISLANDS_TERRITORIES
} from "constants/addresses";
import {
  BAS,
  COMMUNICATION_METHOD_TYPES,
  CONTACT_RELATIONSHIP,
  CRM_SEGMENTS,
  EMPLOYMENT_STATUSES,
  MARITAL_STATUSES,
  PHONE_TYPES
} from "constants/patients";
import TITLES from "constants/titles";
import {
  formatBackendDateOnly,
  formatPhoneNumber,
  getFullName
} from "utils/common";

import {
  EMERGENCY_CONTACT_OPTIONS,
  EMPLOYMENT_OPTIONS,
  PATIENT_NAME_OPTIONS,
  PERSONAL_DETAILS_OPTIONS,
  PRIMARY_CONTACT_OPTIONS,
  SECONDARY_CONTACT_OPTIONS
} from "../merge-tags-options/patient-info";
import { PATIENT_MISCELLANEOUS_OPTIONS } from "../merge-tags-options/patient-info/patient-miscellaneous.utils";
import { PRIMARY_CARE_PHYSICIAN_OPTIONS } from "../merge-tags-options/patient-info/primary-care-physician.utils";
import { REFERRING_PHYSICIAN_OPTIONS } from "../merge-tags-options/patient-info/referring-physician.utils";
import {
  getFormattedDate,
  getPatientSignatureMergeTagOrPlaceholder
} from "../merge-tags.utils";
import { MergeTagsReplaceHandler } from "./merge-tags-values";

export const PATIENT_INFO_VALUES: MergeTagsReplaceHandler = {
  [PATIENT_NAME_OPTIONS.FullName]: ({ data }) => {
    return data.patient?.fullName;
  },
  [PATIENT_NAME_OPTIONS.FirstName]: ({ data }) => {
    return data.patient?.first_name;
  },
  [PATIENT_NAME_OPTIONS.LastName]: ({ data }) => {
    return data.patient?.last_name;
  },
  [PATIENT_NAME_OPTIONS.PreferredName]: ({ data }) => {
    return data.patient?.preferred_name || data.patient?.first_name;
  },
  [PATIENT_NAME_OPTIONS.Title]: ({ data }) => {
    return TITLES.getFromCode(data.patient?.title);
  },
  [PATIENT_NAME_OPTIONS.Suffix]: ({ data }) => {
    return data.patient?.suffix;
  },
  [PATIENT_NAME_OPTIONS.SunoId]: ({ data }) => {
    return String(data.patient?.id ?? "");
  },

  [PERSONAL_DETAILS_OPTIONS.YEARS_OLD]: ({ data }) => {
    return String(data.patient?.age_years);
  },
  [PERSONAL_DETAILS_OPTIONS.DATE_OF_BIRTH]: ({ data }) => {
    return getFormattedDate(data.patient?.birthdate);
  },
  [PERSONAL_DETAILS_OPTIONS.SEX]: ({ data }) => {
    return BAS.getFromCode(data.patient?.sex);
  },
  [PERSONAL_DETAILS_OPTIONS.MARITAL_STATUS]: ({ data }) => {
    return MARITAL_STATUSES.getFromCode(data.patient?.marital_status);
  },
  [PERSONAL_DETAILS_OPTIONS.SSN_NUMBER]: ({ data }) => {
    return data.patient?.ssn;
  },
  //
  [PRIMARY_CONTACT_OPTIONS.NOTES]: ({ data }) => {
    return data.patient?.primary_contact_notes;
  },
  [PRIMARY_CONTACT_OPTIONS.EMAIL_ADDRESS]: ({ data }) => {
    return data.patient?.email;
  },
  [PRIMARY_CONTACT_OPTIONS.PREFERRED_COMMUNICATION_METHOD]: ({ data }) => {
    return COMMUNICATION_METHOD_TYPES.getFromCode(
      data.patient?.preferred_communication_method
    );
  },
  [PRIMARY_CONTACT_OPTIONS.PHONE]: ({ data }) => {
    return formatPhoneNumber(data.patient?.phone ?? "");
  },
  [PRIMARY_CONTACT_OPTIONS.TYPE_OF_PHONE_NUMBER]: ({ data }) => {
    return PHONE_TYPES.getFromCode(data.patient?.phone_type);
  },
  [PRIMARY_CONTACT_OPTIONS.FULL_ADDRESS]: ({ data }) => {
    const patient = data.patient;
    return [
      patient?.street_address_1,
      patient?.street_address_2,
      patient?.city,
      `${patient?.state || ""} ${patient?.zip_code || ""}`.trim()
    ]
      .filter(Boolean)
      .join(", ");
  },
  [PRIMARY_CONTACT_OPTIONS.STREET_ADDRESS_LINE1]: ({ data }) => {
    return data.patient?.street_address_1;
  },
  [PRIMARY_CONTACT_OPTIONS.STREET_ADDRESS_LINE2]: ({ data }) => {
    return data.patient?.street_address_2;
  },
  [PRIMARY_CONTACT_OPTIONS.CITY]: ({ data }) => {
    return data.patient?.city;
  },
  [PRIMARY_CONTACT_OPTIONS.STATE]: ({ data }) => {
    const stateOptions =
      data.patient?.country === CANADA_COUNTRY_VALUE
        ? CANADA_PROVINCES
        : data.patient?.country === US_VIRGIN_ISLANDS_COUNTRY_VALUE
          ? US_VIRGIN_ISLANDS_TERRITORIES
          : US_STATES;
    return stateOptions.getFromCode(data.patient?.state);
  },
  [PRIMARY_CONTACT_OPTIONS.ZIP_CODE]: ({ data }) => {
    return data.patient?.zip_code;
  },

  [SECONDARY_CONTACT_OPTIONS.NOTES]: ({ data }) => {
    return data.patient?.secondary_contact_notes;
  },
  [SECONDARY_CONTACT_OPTIONS.EMAIL_ADDRESS]: ({ data }) => {
    return data.patient?.secondary_email;
  },
  [SECONDARY_CONTACT_OPTIONS.PHONE]: ({ data }) => {
    return formatPhoneNumber(data.patient?.phone ?? "");
  },
  [SECONDARY_CONTACT_OPTIONS.TYPE_OF_PHONE_NUMBER]: ({ data }) => {
    return PHONE_TYPES.getFromCode(data.patient?.secondary_phone_type);
  },

  [SECONDARY_CONTACT_OPTIONS.FULL_ADDRESS]: ({ data }) => {
    const patient = data.patient;
    return [
      patient?.secondary_street_address_1,
      patient?.secondary_street_address_2,
      patient?.secondary_city,
      `${patient?.secondary_state || ""} ${
        patient?.secondary_zip_code || ""
      }`.trim()
    ]
      .filter(Boolean)
      .join(", ");
  },
  [SECONDARY_CONTACT_OPTIONS.STREET_ADDRESS_LINE1]: ({ data }) => {
    return data.patient?.secondary_street_address_1;
  },
  [SECONDARY_CONTACT_OPTIONS.STREET_ADDRESS_LINE2]: ({ data }) => {
    return data.patient?.secondary_street_address_2;
  },
  [SECONDARY_CONTACT_OPTIONS.CITY]: ({ data }) => {
    return data.patient?.city;
  },
  [SECONDARY_CONTACT_OPTIONS.STATE]: ({ data }) => {
    const stateOptions =
      data.patient?.secondary_country === CANADA_COUNTRY_VALUE
        ? CANADA_PROVINCES
        : data.patient?.secondary_country === US_VIRGIN_ISLANDS_COUNTRY_VALUE
          ? US_VIRGIN_ISLANDS_TERRITORIES
          : US_STATES;
    return stateOptions.getFromCode(data.patient?.secondary_state);
  },
  [SECONDARY_CONTACT_OPTIONS.ZIP_CODE]: ({ data }) => {
    return data.patient?.zip_code;
  },

  [EMERGENCY_CONTACT_OPTIONS.RELATIONSHIP_TO_PATIENT]: ({ data }) => {
    return CONTACT_RELATIONSHIP.getFromCode(
      data.patient?.emergency_contact_relationship
    );
  },
  [EMERGENCY_CONTACT_OPTIONS.FULL_NAME]: ({ data }) => {
    return getFullName({
      firstName: data.patient?.emergency_contact_first_name || "",
      lastName: data.patient?.emergency_contact_last_name || ""
    });
  },
  [EMERGENCY_CONTACT_OPTIONS.FIRST_NAME]: ({ data }) => {
    return data.patient?.emergency_contact_first_name;
  },
  [EMERGENCY_CONTACT_OPTIONS.LAST_NAME]: ({ data }) => {
    return data.patient?.emergency_contact_last_name;
  },
  [EMERGENCY_CONTACT_OPTIONS.PHONE_NUMBER]: ({ data }) => {
    return formatPhoneNumber(data.patient?.emergency_contact_phone ?? "");
  },
  [EMERGENCY_CONTACT_OPTIONS.FULL_ADDRESS]: ({ data }) => {
    const patient = data.patient;
    return [
      patient?.emergency_contact_street_address_1,
      patient?.emergency_contact_street_address_2,
      patient?.emergency_contact_city,
      `${patient?.emergency_contact_state || ""} ${
        patient?.emergency_contact_zip_code || ""
      }`.trim()
    ]
      .filter(Boolean)
      .join(", ");
  },
  [EMERGENCY_CONTACT_OPTIONS.STREET_ADDRESS_LINE1]: ({ data }) => {
    return data.patient?.emergency_contact_street_address_1;
  },
  [EMERGENCY_CONTACT_OPTIONS.STREET_ADDRESS_LINE2]: ({ data }) => {
    return data.patient?.emergency_contact_street_address_2;
  },
  [EMERGENCY_CONTACT_OPTIONS.CITY]: ({ data }) => {
    return data.patient?.emergency_contact_city;
  },
  [EMERGENCY_CONTACT_OPTIONS.STATE]: ({ data }) => {
    const stateOptions =
      data.patient?.emergency_contact_country === CANADA_COUNTRY_VALUE
        ? CANADA_PROVINCES
        : data.patient?.emergency_contact_country ===
            US_VIRGIN_ISLANDS_COUNTRY_VALUE
          ? US_VIRGIN_ISLANDS_TERRITORIES
          : US_STATES;
    return stateOptions.getFromCode(data.patient?.emergency_contact_state);
  },
  [EMERGENCY_CONTACT_OPTIONS.ZIP_CODE]: ({ data }) => {
    return data.patient?.emergency_contact_zip_code;
  },

  [EMPLOYMENT_OPTIONS.STATUS]: ({ data }) => {
    return EMPLOYMENT_STATUSES.getFromCode(data.patient?.employment_status);
  },
  [EMPLOYMENT_OPTIONS.EMPLOYER_NAME]: ({ data }) => {
    return data.patient?.employer_name;
  },
  //
  [REFERRING_PHYSICIAN_OPTIONS.FULL_NAME]: ({ data }) => {
    const referringPhysician = data.patient?.referring_physician;

    return getFullName({
      firstName: referringPhysician?.first_name || "",
      lastName: referringPhysician?.last_name || "",
      title: referringPhysician?.title,
      suffix: referringPhysician?.suffix || ""
    });
  },
  [REFERRING_PHYSICIAN_OPTIONS.NPI]: ({ data }) => {
    return data.patient?.referring_physician?.npi;
  },
  [REFERRING_PHYSICIAN_OPTIONS.EMAIL]: ({ data }) => {
    return data.patient?.referring_physician?.email;
  },
  [REFERRING_PHYSICIAN_OPTIONS.PHONE]: ({ data }) => {
    return formatPhoneNumber(data.patient?.referring_physician?.phone ?? "");
  },
  [REFERRING_PHYSICIAN_OPTIONS.FAX_NUMBER]: ({ data }) => {
    return formatPhoneNumber(
      data.patient?.referring_physician?.fax_phone ?? ""
    );
  },
  [REFERRING_PHYSICIAN_OPTIONS.ADDRESS]: ({ data }) => {
    const physician = data.patient?.referring_physician;

    return [
      physician?.street_address_1,
      physician?.street_address_2,
      physician?.city,
      `${physician?.state || ""} ${physician?.zip_code || ""}`.trim()
    ]
      .filter(Boolean)
      .join(", ");
  },
  [REFERRING_PHYSICIAN_OPTIONS.PRACTICE_NAME]: ({ data }) => {
    return data.patient?.referring_physician?.practice_name ?? "";
  },
  //
  [PRIMARY_CARE_PHYSICIAN_OPTIONS.FULL_NAME]: ({ data }) => {
    const primaryCarePhysician = data.patient?.primary_care_physician;

    return getFullName({
      firstName: primaryCarePhysician?.first_name || "",
      lastName: primaryCarePhysician?.last_name || "",
      title: primaryCarePhysician?.title,
      suffix: primaryCarePhysician?.suffix || ""
    });
  },
  [PRIMARY_CARE_PHYSICIAN_OPTIONS.NPI]: ({ data }) => {
    return data.patient?.primary_care_physician?.npi;
  },
  [PRIMARY_CARE_PHYSICIAN_OPTIONS.EMAIL]: ({ data }) => {
    return data.patient?.primary_care_physician?.email;
  },
  [PRIMARY_CARE_PHYSICIAN_OPTIONS.PHONE]: ({ data }) => {
    return formatPhoneNumber(data.patient?.primary_care_physician?.phone ?? "");
  },
  [PRIMARY_CARE_PHYSICIAN_OPTIONS.FAX_NUMBER]: ({ data }) => {
    return formatPhoneNumber(
      data.patient?.primary_care_physician?.fax_phone ?? ""
    );
  },
  [PRIMARY_CARE_PHYSICIAN_OPTIONS.ADDRESS]: ({ data }) => {
    const physician = data.patient?.primary_care_physician;

    return [
      physician?.street_address_1,
      physician?.street_address_2,
      physician?.city,
      `${physician?.state || ""} ${physician?.zip_code || ""}`.trim()
    ]
      .filter(Boolean)
      .join(", ");
  },
  [PRIMARY_CARE_PHYSICIAN_OPTIONS.PRACTICE_NAME]: ({ data }) => {
    return data.patient?.primary_care_physician?.practice_name ?? "";
  },
  //
  [PATIENT_MISCELLANEOUS_OPTIONS.PREFERRED_PROVIDER_FULL_NAME]: ({ data }) => {
    const preferredProvider = data.patient?.preferred_provider;

    return getFullName({
      firstName: preferredProvider?.first_name || "",
      lastName: preferredProvider?.last_name || "",
      title: preferredProvider?.title,
      suffix: preferredProvider?.suffix || ""
    });
  },
  [PATIENT_MISCELLANEOUS_OPTIONS.PREFERRED_CLINIC]: ({ data }) => {
    return data.patient?.preferred_clinic?.display_name;
  },
  [PATIENT_MISCELLANEOUS_OPTIONS.SEGMENT]: ({ data }) => {
    return CRM_SEGMENTS.getFromCode(data.patient?.crm_segment);
  },
  [PATIENT_MISCELLANEOUS_OPTIONS.REFERRAL_SOURCE]: ({ data }) => {
    return data.patient?.referral_source?.name;
  },
  [PATIENT_MISCELLANEOUS_OPTIONS.SUB_REFERRAL_SOURCE]: ({ data }) => {
    return data.patient?.sub_referral_source?.name;
  },
  [PATIENT_MISCELLANEOUS_OPTIONS.TAGS]: ({ data }) => {
    const assignedTags = data.patient?.assigned_tags || [];
    return assignedTags
      .filter((assignedTag) => assignedTag?.tag?.is_active)
      .map((assignedTag) => assignedTag.tag?.name)
      .join(", ");
  },
  [PATIENT_MISCELLANEOUS_OPTIONS.CONSENT_SIGNED_DATE]: ({ data }) => {
    return data.patient?.hipaa_sign_date
      ? formatBackendDateOnly(data.patient.hipaa_sign_date, "MM/dd/yyyy")
      : "Not Signed";
  },
  [PATIENT_MISCELLANEOUS_OPTIONS.HIPAA_SIGNED_DATE]: ({ data }) => {
    return data.patient?.hipaa_sign_date
      ? formatBackendDateOnly(data.patient.hipaa_sign_date, "MM/dd/yyyy")
      : "Not Signed";
  },
  [PATIENT_MISCELLANEOUS_OPTIONS.SIGNATURE]: (
    data,
    allowHtml,
    id,
    patientSignatureURL
  ) => {
    return getPatientSignatureMergeTagOrPlaceholder(patientSignatureURL);
  },
  [PATIENT_MISCELLANEOUS_OPTIONS.PREFERRED_PROVIDER_SIGNATURE]: ({ data }) => {
    return getPatientSignatureMergeTagOrPlaceholder(
      data.patient?.preferred_provider?.signature
    );
  },
  [PATIENT_MISCELLANEOUS_OPTIONS.DIAGNOSIS_CODES]: ({ data }) => {
    return data.patient?.latest_icd10_codes ?? "";
  }
};
